import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Contact as ContactIcon } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useContacts, useContactMutations } from '../hooks/useContacts';
import { ContactFormModal } from '../components/ContactFormModal';
import { ContactDrawer } from '../components/ContactDrawer';
import type { Contact, CreateContactPayload, UpdateContactPayload } from '../types';

export function ContactsPage() {
  // ─── Search (server-side, debounced) ─────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ─── Lightweight client filter (company) ─────────────────────────────────────
  const [company, setCompany] = useState('');

  const filters = useMemo(() => (search ? { search } : {}), [search]);
  const { data: contacts = [], isLoading, isError } = useContacts(filters);
  const { createContact, updateContact, deleteContact } = useContactMutations();

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [drawerContact, setDrawerContact] = useState<Contact | null>(null);
  const [confirmContact, setConfirmContact] = useState<Contact | null>(null);

  // Distinct companies present in the current result set (client filter source)
  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    contacts.forEach((c) => c.company && set.add(c.company));
    return [
      { value: '', label: 'All companies' },
      ...Array.from(set).sort().map((c) => ({ value: c, label: c })),
    ];
  }, [contacts]);

  const visible = useMemo(
    () => (company ? contacts.filter((c) => c.company === company) : contacts),
    [contacts, company],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditContact(null);
    setFormOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setDrawerContact(null);
    setEditContact(contact);
    setFormOpen(true);
  };

  const handleSubmit = (payload: CreateContactPayload | UpdateContactPayload) => {
    if (editContact) {
      updateContact.mutate(
        { id: editContact.id, payload: payload as UpdateContactPayload },
        {
          onSuccess: () => {
            toast.success('Contact updated.');
            setFormOpen(false);
            setEditContact(null);
          },
          onError: () => toast.error('Failed to update contact.'),
        },
      );
    } else {
      createContact.mutate(payload as CreateContactPayload, {
        onSuccess: () => {
          toast.success('Contact created.');
          setFormOpen(false);
        },
        onError: () => toast.error('Failed to create contact.'),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmContact) return;
    deleteContact.mutate(confirmContact.id, {
      onSuccess: () => {
        toast.success('Contact deleted.');
        setConfirmContact(null);
        setDrawerContact(null);
      },
      onError: () => toast.error('Failed to delete contact.'),
    });
  };

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (c) => (
        <div className="flex items-center gap-3">
          <Avatar name={c.company || c.name} size="sm" />
          <div className="min-w-0">
            <div className="truncate font-medium text-on-surface">{c.name}</div>
            {c.position && <div className="truncate text-body-sm text-on-surface-variant">{c.position}</div>}
          </div>
        </div>
      ),
    },
    { key: 'company', header: 'Company', render: (c) => c.company || '—' },
    {
      key: 'email',
      header: 'Email',
      render: (c) =>
        c.email ? (
          <a
            href={`mailto:${c.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary hover:underline"
          >
            {c.email}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (c) =>
        c.phone ? (
          <a
            href={`tel:${c.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="tabular-nums text-on-surface-variant hover:text-primary"
          >
            {c.phone}
          </a>
        ) : (
          '—'
        ),
    },
    { key: 'creator_name', header: 'Owner', render: (c) => c.creator_name || '—' },
  ];

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ContactIcon className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Contacts</h1>
            <p className="text-body-sm text-on-surface-variant">
              {visible.length} contact{visible.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} size="md">
          <Plus className="h-4 w-4" />
          New Contact
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64">
          <Input
            name="contacts-search"
            placeholder="Search name, company, email, phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="w-52">
          <Select
            name="contacts-company-filter"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            options={companyOptions}
          />
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load contacts" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || company ? 'No matching contacts' : 'No contacts yet'}
          description={
            search || company ? 'Try adjusting your search or filter.' : 'Create your first contact to build the customer database.'
          }
          action={!search && !company ? <Button onClick={handleCreate}>New Contact</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <Table columns={columns} data={visible} rowKey={(c) => c.id} onRowClick={setDrawerContact} empty="No contacts found." />
        </div>
      )}

      {/* Create / Edit */}
      <ContactFormModal
        open={formOpen}
        contact={editContact}
        loading={createContact.isPending || updateContact.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditContact(null);
        }}
        onSubmit={handleSubmit}
      />

      {/* Detail */}
      <ContactDrawer
        contact={drawerContact}
        deleting={deleteContact.isPending}
        onClose={() => setDrawerContact(null)}
        onEdit={handleEdit}
        onDelete={setConfirmContact}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!confirmContact}
        title="Delete contact"
        message={`Delete "${confirmContact?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteContact.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmContact(null)}
      />
    </div>
  );
}
